import { Workbook } from "exceljs";
import * as Papa from "papaparse";
import { Injectable, inject } from "@angular/core";
import { DatePipe } from "@angular/common";
import { Int_Excel_Export_File_Config, Int_ExcelDataFields } from "../models/components";

@Injectable({
  providedIn: "root",
})
export class FunctionalityService {
  private readonly datePipe = inject(DatePipe);

  transformExcelItems(items: any[], fields: Int_ExcelDataFields[]): any[] {
    const formatValidDate = (date: any, type: string): string | null => {
      const parsedDate = new Date(date);

      if (isNaN(parsedDate.getTime())) {
        // Return null if the date is invalid
        return null;
      }

      // Use different formats for "date" and "date-time"
      const format = type === "date-time" ? "d-MMM-yy, h:mm a" : "d-MMM-yy";
      return this.datePipe.transform(parsedDate, format);
    };

    return items.map((el: any) => {
      const transformedItem: any = { ...el };

      fields.forEach(field => {
        if (field.FieldType === "date-time" && el[field.key]) {
          transformedItem[field.key] = formatValidDate(el[field.key], "date-time");
        } else if (field.FieldType === "date" && el[field.key]) {
          transformedItem[field.key] = formatValidDate(el[field.key], "date");
        }
      });

      return transformedItem;
    });
  }

  xlsxExport(fileConfig: Int_Excel_Export_File_Config) {
    if (!fileConfig.items.length) return;

    let filteredList: any[] = fileConfig.items;

    if (fileConfig.columns && fileConfig.columns.length) {
      filteredList = fileConfig.items.map((el: any) => {
        const data: any = {};
        fileConfig.columns?.forEach((colData: Int_ExcelDataFields) => {
          data[colData.label] = el[colData.key]; // Map key to label
        });
        return data;
      });
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");

    // Add header row using Headings
    const headerRow = fileConfig.columns?.map(col => col.label) || Object.keys(filteredList[0]);
    const row = worksheet.addRow(headerRow);
    row.eachCell(cell => {
      cell.font = { bold: true };
    });

    // Add data rows
    filteredList.forEach(item => {
      const rowValues = fileConfig.columns?.map(col => item[col.label]) || Object.values(item);
      worksheet.addRow(rowValues);
    });

    // Write the file and trigger download
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileConfig.fileName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 0);
    });
  }

  csvExport(fileConfig: Int_Excel_Export_File_Config) {
    if (!fileConfig.items.length) return;

    let filteredList: any[] = fileConfig.items;

    if (fileConfig.columns && fileConfig.columns.length) {
      filteredList = fileConfig.items.map((el: any) => {
        const data: any = {};
        fileConfig.columns?.forEach((colData: Int_ExcelDataFields) => {
          data[colData.label] = el[colData.key]; // Map FieldName to Heading
        });
        return data;
      });
    }

    const csvData = Papa.unparse(filteredList, {
      header: true,
      columns: fileConfig.columns?.map(col => col.label) || Object.keys(filteredList[0]),
    });

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileConfig.fileName}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 0);
  }
}
